<template>
  <div>
    <div class="pa-5 d-flex justify-space-between align-center">
      <h1>Artikelomzet per categorie</h1>
    </div>

    <!-- Filters Panel -->
    <div class="d-flex flex-wrap align-center px-5 py-2 mb-4 elevation-1 rounded" :style="panelStyle">
      <div class="header-input">
        <date-picker :dense="true" :outlined="true" :filled="true" :value="fromDate" label="Datum"
          :dateFormatted="fromDateFormatted" @changeDate="changeFromDate"></date-picker>
      </div>
      <div class="header-input">
        <date-picker :dense="true" :outlined="true" :filled="true" :value="toDate" label="t/m"
          :dateFormatted="toDateFormatted" @changeDate="changeToDate"></date-picker>
      </div>
      <div class="header-input-small">
        <v-select :items="years" v-model="year" label="Jaar" outlined dense hide-details class="mb-4"></v-select>
      </div>
      <div class="header-input-small">
        <v-select :items="quarters" v-model="quarter" label="Kwartaal" outlined dense hide-details class="mb-4"></v-select>
      </div>
      <div class="header-input-small">
        <v-select :items="months" item-text="text" item-value="value" v-model="month" label="Maand" outlined dense hide-details class="mb-4"></v-select>
      </div>
      <div class="header-input">
        <search-client :searchString="searchClientString" :noResults="false" :filled="false"
          @setSearchString="setSearchClientString" @get-results="selectClient"
          @clear-input="clearClient"></search-client>
      </div>
      <div class="d-flex no-print mb-4 ml-auto">
        <v-btn color="primary" class="mr-2" @click="fetchCategoryRevenue">Verzend</v-btn>
        <v-btn color="grey" class="mr-2" @click="reset">Reset</v-btn>
        <v-btn color="info" @click="print">Afdrukken</v-btn>
      </div>
    </div>

    <!-- Content Row -->
    <v-row>
      <!-- Pie Chart Card -->
      <v-col cols="12" md="6">
        <v-card class="pa-4 fill-height d-flex flex-column justify-space-between" min-height="450">
          <div class="d-flex justify-space-between align-center mb-4">
            <span class="text-h6 font-weight-bold">Visualisatie</span>
            <v-btn-toggle v-model="selectedMetric" mandatory dense color="primary" @change="updateChartData">
              <v-btn value="revenue">Omzet (€)</v-btn>
              <v-btn value="amount">Aantal</v-btn>
            </v-btn-toggle>
          </div>
          <div class="flex-grow-1 position-relative" style="min-height: 320px;">
            <canvas id="categoryPieChart"></canvas>
          </div>
        </v-card>
      </v-col>

      <!-- Details Table Card -->
      <v-col cols="12" md="6">
        <v-card class="pa-4 fill-height d-flex flex-column" min-height="450">
          <span class="text-h6 font-weight-bold mb-4">Details per categorie</span>
          <v-data-table
            :items="items"
            :headers="headers"
            :items-per-page="10"
            class="elevation-0"
            item-key="category"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <template v-slot:[`item.amount`]="{ item }">
              {{ toDouble(item.amount) }}
            </template>
            <template v-slot:[`item.revenue`]="{ item }">
              {{ toCurrency(item.revenue) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { fetchGET, toCurrency, toDouble } from '../js/functions'
import datePicker from '../components/datePicker.vue'
import searchClient from '../components/searchClient.vue'
import {
  Chart,
  PieController,
  ArcElement,
  Tooltip,
  Legend
} from "chart.js"

Chart.register(
  PieController,
  ArcElement,
  Tooltip,
  Legend
)

export default {
  name: "itemsRevenueByCategory",
  data() {
    return {
      items: [],
      year: null,
      month: null,
      quarter: null,
      fromDate: new Date(Date.now()).toISOString().split("T")[0],
      toDate: new Date(Date.now()).toISOString().split("T")[0],
      fromDateFormatted: this.formatDate(new Date().toISOString().split("T")[0]),
      toDateFormatted: this.formatDate(new Date().toISOString().split("T")[0]),
      years: [2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026],
      quarters: [1, 2, 3, 4],
      months: [
        { text: "Januari", value: 1 },
        { text: "Februari", value: 2 },
        { text: "Maart", value: 3 },
        { text: "April", value: 4 },
        { text: "Mei", value: 5 },
        { text: "Juni", value: 6 },
        { text: "Juli", value: 7 },
        { text: "Augustus", value: 8 },
        { text: "September", value: 9 },
        { text: "Oktober", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ],
      selectedClient: {},
      searchClientString: "",
      selectedMetric: "revenue",
      sortBy: "revenue",
      sortDesc: true,
      headers: [
        { text: "Categorie", value: "category", sortable: true },
        { text: "Aantal verkocht", value: "amount", align: "end", sortable: true },
        { text: "Omzet", value: "revenue", align: "end", sortable: true },
      ],
      chart: null,
      chartData: {
        labels: [],
        datasets: [
          {
            label: "Omzet per categorie",
            data: [],
            backgroundColor: [
              "#2A4B7C", // Dark Blue
              "#3E8E7E", // Teal/Sage
              "#E07A5F", // Terracotta
              "#81B29A", // Soft Green
              "#F2CC8F", // Pale Gold
              "#3D405B", // Slate
              "#7209B7", // Violet
              "#4895EF", // Light Blue
              "#560BAD", // Deep Purple
              "#FF9F1C", // Orange
              "#2EC4B6", // Turquoise
              "#D62828"  // Red
            ],
            borderWidth: 1
          }
        ]
      }
    }
  },
  computed: {
    panelStyle() {
      return {
        background: this.$vuetify.theme.dark ? '#1e1e1e' : '#ffffff',
        border: `1px solid ${this.$vuetify.theme.dark ? '#333' : '#e0e0e0'}`
      }
    }
  },
  methods: {
    toDouble(val) {
      return toDouble(val)
    },
    toCurrency(val) {
      return toCurrency(val)
    },
    changeFromDate(date) {
      this.fromDate = date
      this.fromDateFormatted = this.formatDate(date)
      if (this.fromDate > this.toDate) {
        this.toDate = this.fromDate
        this.toDateFormatted = this.fromDateFormatted
      }
      this.fetchCategoryRevenue()
    },
    changeToDate(date) {
      this.toDate = date
      this.toDateFormatted = this.formatDate(date)
      if (this.toDate < this.fromDate) {
        this.fromDate = this.toDate
        this.fromDateFormatted = this.toDateFormatted
      }
      this.fetchCategoryRevenue()
    },
    formatDate(date) {
      if (!date) return null
      const [year, month, day] = date.split("-")
      return `${day}-${month}-${year}`
    },
    async fetchCategoryRevenue() {
      try {
        let response = await fetchGET("fetchItemsRevenueByCategory", {
          year: this.year,
          month: this.month,
          quarter: this.quarter,
          fromDate: this.fromDate,
          toDate: this.toDate,
          clientNumber: this.selectedClient.clientNumber,
        })
        this.items = response || []
        this.updateChartData()
      }
      catch (e) {
        console.error(e)
      }
    },
    updateChartData() {
      const labels = []
      const data = []
      this.items.forEach(item => {
        labels.push(item.category || "Overig")
        if (this.selectedMetric === "revenue") {
          data.push(item.revenue)
        } else {
          data.push(item.amount)
        }
      })
      this.chartData.labels = labels
      this.chartData.datasets[0].data = data
      this.chartData.datasets[0].label = this.selectedMetric === "revenue" ? "Omzet (€)" : "Aantal"
      
      if (this.chart != null) this.chart.destroy()
      this.createChart()
    },
    createChart() {
      this.$nextTick(() => {
        const canvas = document.getElementById("categoryPieChart")
        if (!canvas) return
        const ctx = canvas.getContext("2d")
        this.chart = new Chart(ctx, {
          type: "pie",
          data: this.chartData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  color: this.$vuetify.theme.dark ? '#ffffff' : '#333333',
                  font: {
                    family: 'Outfit, Roboto, sans-serif',
                    size: 13
                  }
                }
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.label || ''
                    const value = context.parsed
                    if (this.selectedMetric === 'revenue') {
                      return ` ${label}: ${this.toCurrency(value)}`
                    } else {
                      return ` ${label}: ${this.toDouble(value)}`
                    }
                  }
                }
              }
            }
          }
        })
      })
    },
    reset() {
      this.year = null
      this.month = null
      this.quarter = null
      this.fromDate = new Date(Date.now()).toISOString().split("T")[0]
      this.toDate = new Date(Date.now()).toISOString().split("T")[0]
      this.fromDateFormatted = this.formatDate(this.fromDate)
      this.toDateFormatted = this.formatDate(this.toDate)
      this.selectedClient = {}
      this.searchClientString = ""
      this.fetchCategoryRevenue()
    },
    print() {
      window.print()
    },
    setSearchClientString(e) {
      this.searchClientString = e
    },
    selectClient(e) {
      this.selectedClient = e
      this.fetchCategoryRevenue()
    },
    clearClient() {
      this.searchClientString = ""
      this.selectedClient = {}
      this.fetchCategoryRevenue()
    }
  },
  mounted() {
    this.fetchCategoryRevenue()
  },
  beforeDestroy() {
    if (this.chart != null) {
      this.chart.destroy()
    }
  },
  components: {
    datePicker,
    searchClient
  }
}
</script>

<style scoped>
.header-input {
  padding: 0 8px;
  min-width: 200px;
  max-width: 300px;
}

.header-input-small {
  padding: 0 8px;
  width: 130px;
}

.position-relative {
  position: relative;
}
</style>
